/**
 * Recover from stale lazy-chunk loads after a new frontend deploy.
 *
 * The app lazy-loads route components via dynamic `import()`, which Vite emits as
 * hash-named chunks (e.g. `AuthUser-ByUlQSRr.js`). Netlify's atomic deploys replace
 * every file instantly with no grace period, so a tab opened before a deploy still
 * references the old hashes. When such a tab navigates and triggers a lazy import,
 * the old chunk is gone and the browser throws:
 *
 *   TypeError: Failed to fetch dynamically imported module: .../js/AuthUser-<hash>.js
 *
 * Because filenames are hashed, that request 404s (rather than silently serving new
 * code), which makes the failure catchable. Vite dispatches a `vite:preloadError`
 * event for it. Here we catch it and reload once so the tab picks up the new asset
 * manifest, guarding against reload loops via a short-lived sessionStorage flag.
 *
 * We deliberately do NOT call `event.preventDefault()` on `vite:preloadError`. Vite's
 * preload helper ends with `baseModule().catch(handlePreloadError)`, and
 * `handlePreloadError` only rethrows when the event was not cancelled — so cancelling it
 * makes the failed import *resolve with `undefined`* instead of rejecting. Because
 * `location.reload()` is asynchronous, awaiting callers then resume with that `undefined`
 * and fail somewhere unrelated (Sentry ACTIVIX-CRM-WEB-P89: `Cannot destructure property
 * 'default'` in `store/actions.js`). Letting Vite rethrow keeps the import rejecting, so
 * existing error paths still work; the expected Sentry noise from the dying page is
 * dropped in `plugins/sentry.ts` via `isStaleChunkReloadPending()`.
 *
 * See also the matching `router.onError` handler in `src/router/index.ts`, which
 * covers the same failure surfaced through vue-router navigation.
 */

import {
    observeAssetFailure,
    observeAssetReload,
    observeAssetRecoveryBlocked,
} from '@/telemetry/assetRecovery.ts';

const RELOAD_FLAG = 'chunk-reload:last-attempt';
// Ignore a stored flag older than this, so a genuine (non-deploy) failure can still
// trigger a fresh reload attempt later instead of being suppressed forever.
const RELOAD_WINDOW_MS = 30_000;

// Intentionally in-memory rather than in sessionStorage: a real reload gives us a fresh
// module instance, so this only ever reports true for the page being torn down. The
// sessionStorage marker outlives the reload and would wrongly suppress errors on the
// recovered page.
let reloadPending = false;

/**
 * Whether this page has already initiated a stale-chunk recovery reload. Errors raised
 * after that point come from a page that is about to be replaced.
 */
export function isStaleChunkReloadPending(): boolean {
    return reloadPending;
}

/**
 * Reload the page once to recover from a stale-chunk failure.
 * Returns `false` if a reload was already attempted recently (loop guard).
 */
export function reloadForStaleChunk(): boolean {
    const now = Date.now();

    // The loop guard relies on persisting a marker across the reload. If sessionStorage
    // is unavailable (private mode, blocked/sandboxed storage), we cannot guarantee a
    // once-only reload, so we must NOT reload — otherwise a persistent failure would
    // reload the page indefinitely. Let the error surface instead.
    let storage: Storage;
    let last = 0;

    try {
        storage = globalThis.sessionStorage;
        last = Number(storage.getItem(RELOAD_FLAG)) || 0;
    } catch {
        observeAssetRecoveryBlocked('storage_read');
        return false;
    }

    if (last && now - last < RELOAD_WINDOW_MS) {
        // Already reloaded recently and still failing — the new chunk is genuinely
        // missing. Stop reloading to avoid an infinite loop; let the error surface.
        observeAssetRecoveryBlocked('cooldown');
        return false;
    }

    try {
        storage.setItem(RELOAD_FLAG, String(now));
    } catch {
        // Cannot persist the guard marker — reloading now would risk an infinite loop.
        observeAssetRecoveryBlocked('storage_write');
        return false;
    }

    reloadPending = true;
    observeAssetReload();
    globalThis.location.reload();
    return true;
}

/**
 * Detect the "failed to fetch a dynamically imported module" family of errors,
 * across the different messages browsers use (Chrome/Edge, Firefox, Safari).
 */
export function isChunkLoadError(error: unknown): boolean {
    let message = '';
    if (error instanceof Error) {
        message = error.message;
    } else if (typeof error === 'string') {
        message = error;
    }

    return (
        /Failed to fetch dynamically imported module/i.test(message) ||
        /error loading dynamically imported module/i.test(message) ||
        /Importing a module script failed/i.test(message)
    );
}

// Vite fires this when a preloaded/lazy chunk fails to load. Never cancel the event (see
// the note at the top of this file) — we only start the recovery reload and let Vite
// rethrow, so the pending import rejects rather than resolving with `undefined`.
globalThis.addEventListener('vite:preloadError', (event) => {
    observeAssetFailure((event as Event & { payload?: unknown }).payload, 'vite');
    reloadForStaleChunk();
});
